<template>
  <div class="g-gantt-row" ref="g-gantt-row" :style="{ height: `${$parent.rowHeight}px` }" v-on="$listeners">
    <div class="g-gantt-row-label text-black" :style="rowLabelStyle">
      <slot name="label">
      </slot>
    </div>
    <div class="g-gantt-row-bars-container" :id="'row' + rowN" ref="barContainer" :style="barsContainerStyle"
      @dragover="onDragover($event)" @dragleave="onDragleave($event)" @drop="onDrop($event)" @mouseover="onMouseover()"
      @mouseleave="onMouseleave()" @click.stop="closePopover(null)">
      <template v-for="bar in barsList">
        <g-gantt-bar v-if="checkTimesHasOverlap(bar.plan_starttime, bar.plan_endtime)" :rowcount="rowcount"
          :key="`ganttastic_bar_${bar.id}`" :bar="bar" ref="ganttBar" :row-n="rowN" :bar-start="barStart"
          :bar-end="barEnd" :bar-container="barContainer" :all-bars-in-row="barsList" @closePopover="closePopover"
          @update="$emit('update')" v-bind="$attrs">
          <template #bar-label="{ bar }">
            <slot name="bar-label" :bar="bar" />
          </template>
        </g-gantt-bar>
      </template>
    </div>
    <div :class="row.showSlide && 'slide-animate'"></div>
  </div>
</template>

<script>
import GGanttBar from './GGanttBar.vue'
import moment from 'moment'
import Vue from 'vue'
export default {
  name: 'GGanttRow',

  components: {
    GGanttBar
  },

  props: {
    rowcount: Number,
    label: { type: String, default: 'Row' },
    bars: { type: Array, default: () => [] },
    barStart: { type: String, required: true }, // property name of the bar objects that represents the start datetime
    barEnd: { type: String, required: true }, // property name of the bar objects that represents the end datetime,
    highlightOnHover: Boolean,
    row: {
      type: Object
    },
    dragid: {
      type: Number
    },
    rowN: {
      type: Number
    }
  },

  inject: ['ganttChartProps', 'getThemeColors', 'getHourCount', 'getChartStart', 'getChartEnd'],

  data () {
    return {
      barContainer: {}
    }
  },

  computed: {
    barsList: {
      get () {
        return this.bars
      },
      set (value) {
        Vue.set(this.bars, this.bars.length, value)
      }
    },
    rowLabelStyle () {
      return {
        width: this.ganttChartProps.rowLabelWidth,
        height: this.ganttChartProps.rowHeight,
        background: this.$parent.themeColors.ternary
      }
    },

    barsContainerStyle () {
      return {
        width: `${100 - this.ganttChartProps.rowLabelWidth.replace('%', '')}%`,
        backgroundColor: this.row.rowConfig.display ? '' : '#000'
      }
    }
  },

  created () {
    this.row.id = this.rowN
  },
  mounted () {
    this.barContainer = this.$refs.barContainer.getBoundingClientRect()
    window.addEventListener('resize', this.onWindowResize)
    this.$emit('barContainer', this.barContainer)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.onWindowResize)
  },

  methods: {
    closePopover (e) {
      this.$emit('closePopover', e)
    },
    onDragover (e) {
      e.preventDefault() // enables dropping content on row
      if (this.highlightOnHover) {
        this.$refs['g-gantt-row'].style.backgroundColor = this.getThemeColors().hoverHighlight
      }
    },

    onDragleave () {
      this.$refs['g-gantt-row'].style.backgroundColor = null
    },
    refreshClient () {
      this.barContainer = this.$refs.barContainer.getBoundingClientRect()
    },
    onDrop (e) {
      let barContainer = this.$refs.barContainer.getBoundingClientRect()
      let xPos = e.clientX - barContainer.left
      let hourDiffFromStart = (xPos / barContainer.width) * this.getHourCount()
      let time = moment(this.getChartStart()).add(hourDiffFromStart, 'hours')
      let bar = this.barsList.find(bar => time.isBetween(bar[this.barStart], bar[this.barEnd]))
      this.$emit('drop', { event: e, bar, time: time.format('YYYY-MM-DD HH:mm:ss') })
    },

    onMouseover () {
      if (this.highlightOnHover) {
        this.$refs['g-gantt-row'].style.backgroundColor = this.getThemeColors().hoverHighlight
      }
    },

    onMouseleave () {
      this.$refs['g-gantt-row'].style.backgroundColor = null
    },

    onWindowResize () {
      // re-initialize the barContainer DOMRect variable, which will trigger re-rendering in the gantt bars
      this.barContainer = this.$refs.barContainer.getBoundingClientRect()
      this.$emit('barContainer', this.barContainer)
    },
    checkTimesHasOverlap (dynaStartTime, dynaEndTime) {
      return !(new Date(dynaEndTime).getTime() < new Date(this.getChartStart()).getTime() || new Date(dynaStartTime).getTime() > new Date(this.getChartEnd()).getTime());
    }
  },

  watch: {
    'ganttChartProps.rowLabelWidth': function () {
      this.barContainer = this.$refs.barContainer.getBoundingClientRect()
      this.$emit('barContainer', this.barContainer)
    }
  }
}
</script>

<style lang="scss" scoped>
.g-gantt-row {
  display: flex;
  width: 100%;
  height: 40px;
  /* flex-direction: column; */
  // overflow: hidden;
  transition: background-color 0.2s;
}

.g-gantt-row>.g-gantt-row-label {
  display: flex;
  align-items: center;
  padding: 10px 10px;
  background: #e8e8e8;
  color: #424242;
  font-size: 0.9em;
  z-index: 3;
  border-bottom: 1px solid #eaeaea;
  border-left: 1px solid #eaeaea;
  border-collapse: collapse;
  overflow: hidden;
  font-weight: bold;
}

.g-gantt-row>.g-gantt-row-bars-container {
  position: relative;
  width: 70%;
  border-bottom: 1px solid #eaeaea;
  border-collapse: collapse;
}

.slide-animate {
  position: absolute;
  left: 0px;
  // left: 0;
  width: 10%;
  height: 40px;
  // filter: blur(30px);
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 9999;
  // animation: slide 0.8s 0.1s 1;
}

@keyframes slide {
  0% {
    left: -10%;
  }

  100% {
    left: 150%;
  }
}
</style>